import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";
import type { ApiScheduledEmail } from "@shared/types/models";
import type { ScheduleEmailFields } from "../../forms/emails/scheduleEmailForm";

export const useScheduledEmails = () => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState({
    scheduledEmails: false,
    create: false,
    cancel: false,
  });

  const loadScheduledEmails = async (
    setScheduledEmails: (data: ApiScheduledEmail[]) => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, scheduledEmails: true }));
      const response = await api(
        "/scheduled-emails",
        { method: "GET" },
        dispatch,
      );
      setScheduledEmails(response.scheduledEmails);
    } catch {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, scheduledEmails: false }));
    }
  };

  const createScheduledEmail = async (
    data: ScheduleEmailFields,
    onSuccess: () => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, create: true }));
      const sendAt = new Date(`${data.date}T${data.time}`);
      const payload = {
        subject: data.subject,
        body: data.body,
        audience: { type: "all-students" as const },
        sendAt: sendAt.toISOString(),
      };
      const response = await api(
        "/scheduled-emails",
        { method: "POST", body: payload },
        dispatch,
      );
      toast.success(response.message);
      onSuccess();
      return response.scheduledEmail as ApiScheduledEmail;
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const cancelScheduledEmail = async (id: string) => {
    try {
      setLoading((prev) => ({ ...prev, cancel: true }));
      const response = await api(
        `/scheduled-emails/${id}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success(response.message);
      return response.scheduledEmail as ApiScheduledEmail;
    } catch (error: any) {
      toast.error(error.message);
      return undefined;
    } finally {
      setLoading((prev) => ({ ...prev, cancel: false }));
    }
  };

  return {
    loadScheduledEmails,
    createScheduledEmail,
    cancelScheduledEmail,
    loading,
  };
};
